import {
  type InitAvo,
  isSfccHostedPage,
  normalizeAnalyticsEnvironment,
  registerAnalytics,
} from '@susu/analytics-client'

import { version } from '../../../package.json'
import Avo from '../../avo/avo'
import type { Environment } from '../config'
import { initDatadog } from '../datadog'

export interface PreferenceCenterAnalyticsConfig {
  /**
   * Whether this mount owns the page. Preference Center runs standalone on its
   * own app, and composed inside Customer Hub and the storefront. Defaults to
   * `false`, the safe direction, except on an SFCC-rendered page.
   */
  ownsPage?: boolean
  /**
   * @deprecated Release only, ignored. Customer Hub and the storefront still
   * pass their pre-FC-183 config here until their own DA-1252 PRs land. Both
   * load OneTrust themselves, and the client derives the GA4 id from the
   * surface. Removed by the stack's last PR.
   */
  oneTrustId?: string
  /** @deprecated Release only, ignored. See `oneTrustId`. */
  trackingGaCookie?: string
}

/** Suppresses default Avo `[avo] ...` console output from the generated client. */
const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const initAvo: InitAvo = (
  options,
  systemProperties,
  destinationOptions,
  segmentDestination,
) => {
  Avo.initAvo(
    {
      env: options.env === 'prod' ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
      webDebugger: false,
      avoLogger: silentAvoLogger,
    },
    systemProperties as unknown as Parameters<typeof Avo.initAvo>[1],
    destinationOptions,
    segmentDestination as Parameters<typeof Avo.initAvo>[3],
  )
}

export const initializeAnalytics = (
  env: Environment,
  analyticsConfig: PreferenceCenterAnalyticsConfig = {},
): Promise<(() => void) | undefined> => {
  registerAnalytics({
    surface: {
      // A consumer-facing page: OneTrust gates the transport and the events go
      // to the Website source.
      placement: 'website',
      environment: normalizeAnalyticsEnvironment(env),
      // Embedded hosts (Customer Hub, the storefront) already own the
      // transport and the banner; only the standalone app is the page owner.
      // The SFCC bundle passes no config, and nothing on an SFCC-rendered
      // page calls initializeAnalytics, so there it owns the page too, or its
      // events would queue forever (same fix as #7508 for CM Lite).
      ownsPage: analyticsConfig.ownsPage ?? isSfccHostedPage(),
    },
    applicationId: Avo.ApplicationId.PREFERENCE_CENTER,
    version,
    initAvo,
    setAvoSystemProperties: Avo.setSystemProperties as (
      properties: Record<string, unknown>,
    ) => void,
  })

  return initDatadog(env)
}
